import { sortQualityAlternates } from "./confidence"
import type {
  QuestionQualityAlternate,
  QuestionQualityCheck,
  QuestionQualityHistory,
} from "./types"

export type QuestionQualityOpportunityKind =
  | "apply"
  | "leo"
  | "settings"
  | "replace"

export interface QuestionQualityOpportunity {
  id: string
  label: string
  kind: QuestionQualityOpportunityKind
  checkId?: string
  alternate?: QuestionQualityAlternate
}

const LABEL_FOR_CHECK: Partial<Record<string, string>> = {
  "compliance-incomplete-stem": "Rewrite the stem as a complete question",
  "compliance-missing-reference": "Add the missing passage",
  "clarity-fragment-stem": "Rewrite the stem as a complete question",
  "clarity-unmarked-negative": "Mark NOT or EXCEPT",
  "clarity-reading-level": "Simplify the stem",
  "clarity-title-mismatch": "Align the title with the stem",
  "clarity-short-title": "Give the item a searchable title",
  "distractor-keyed-nota": "Replace None of the above with a real key",
  "distractor-aota-nota": "Replace All of the above and None of the above",
  "distractor-too-few": "Write three functional distractors",
  "cueing-longest-key": "Balance key length with the distractors",
  "cueing-grammar-key": "Rewrite options that cue the key",
  "compliance-no-rationale": "Add a rationale for the key",
  "format-select-all": "Change format",
  "format-misfit": "Change format",
  "classification-bloom": "Set Bloom’s",
  "classification-difficulty": "Set difficulty",
}

function labelForCheck(check: QuestionQualityCheck): string {
  if (LABEL_FOR_CHECK[check.id]) return LABEL_FOR_CHECK[check.id]!
  if (check.id.startsWith("distractor-unstated-")) {
    return "Write three functional distractors"
  }
  if (check.actionKind === "open-leo") return check.title
  return check.actionLabel
}

export function questionQualityOpportunities(
  checks: QuestionQualityCheck[],
  history: QuestionQualityHistory | null,
): QuestionQualityOpportunity[] {
  const rows: QuestionQualityOpportunity[] = []
  const seen = new Set<string>()

  const push = (row: QuestionQualityOpportunity) => {
    if (seen.has(row.label)) return
    seen.add(row.label)
    rows.push(row)
  }

  for (const check of checks) {
    const ranked = sortQualityAlternates(check.alternates ?? [], checks)
    const applyable = ranked.find(
      (item) => item.actionKind !== "none" && item.actionKind !== "open-leo",
    )
    if (applyable && applyable.clearsCheckIds.length > 0) {
      push({
        id: `apply-${check.id}-${applyable.id}`,
        label: labelForCheck(check),
        kind: "apply",
        checkId: check.id,
        alternate: applyable,
      })
      continue
    }
    if (check.actionKind === "none" && check.parameter === "format") {
      push({
        id: `format-${check.id}`,
        label: "Change format",
        kind: "settings",
        checkId: check.id,
      })
      continue
    }
    if (check.actionKind === "open-leo" || check.actionKind === "none") {
      push({
        id: `leo-${check.id}`,
        label: labelForCheck(check),
        kind: check.actionKind === "none" ? "settings" : "leo",
        checkId: check.id,
      })
    }
  }

  if (history?.belowReviewFloor) {
    push({
      id: "replace-from-bank",
      label: "Replace from bank",
      kind: "replace",
    })
  }

  return rows
}
